import { html, nothing, TemplateResult } from 'lit';
import { customElement, property, query, queryAll } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_HEALTH_DASHBOARD_PATCH_MANAGEMENT } from '../../../../webcomponents.constants';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { HealthDashboardPatchManagementStrings, PatchManagementItem, StringSection } from '../types';
import { translateFn } from '../../../../utils/translator.helper';
import { HealthDashboardInfoStateData, PatchManagementInfo } from '@nirvana/shared/dtos/types/widgets/common';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { getTranslatedWidgetTitle } from '../../../../genericDashboard/utils/translations';
import { removeElementClass, setElementClass } from '../../../../utils/htmlElement.utils';
import { WidgetHeaderItemSize } from '../../../../ui/gzWidgetHeaderItem/types';

@customElement(WEB_COMPONENT_HEALTH_DASHBOARD_PATCH_MANAGEMENT)
export class PatchManagementWebComponent extends GzGenericWidgetContent {
  @property({ type: Object, attribute: true, reflect: true })
  payload: { patchManagement: PatchManagementInfo | HealthDashboardInfoStateData };

  changedInfoBoxText = `
          ${translateFn('You have <b>updates</b> for this widget!')}
          <br />
          <br />
          ${translateFn(
            'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
          )}`;

  // for sections and item key names will be changed by team Micky
  strings: HealthDashboardPatchManagementStrings = {
    sections: [
      {
        key: 'overview',
        items: [
          {
            key: 'updated',
            name: translateFn('Up to date'),
            tooltipText: translateFn('Endpoints are up to date, and no new patches are currently available.'),
            barColor: 'var(--green-teal-chart-300)',
          },
          {
            key: 'pendingRestart',
            name: translateFn('Require reboot'),
            tooltipText: translateFn('Endpoints require a system restart to complete the patch installation process.'),
            barColor: 'var(--yellow-alert-050)',
          },
          {
            key: 'downloadFailed',
            name: translateFn('Patch download failed'),
            tooltipText: translateFn('Patches could not be downloaded on endpoints.'),
            barColor: 'var(--orange-alert-chart-100)',
          },
          {
            key: 'installationFailed',
            name: translateFn('Patch installation failed'),
            tooltipText: translateFn('Patches could not be successfully installed on endpoints.'),
            barColor: 'var(--orange-alert-chart-100)',
          },
          {
            key: 'notInstalled',
            name: translateFn('Patches available, not installed'),
            tooltipText: translateFn(
              'Endpoints have checked for available patches, but no patch installation task was initiated.'
            ),
            barColor: 'var(--orange-alert-chart-100)',
          },
        ],
      },
      {
        key: 'assessmentInfo',
        items: [
          {
            key: 'neverChecked',
            name: translateFn('Never checked available patches'),
            tooltipText: translateFn(
              'Endpoints never checked for patches. Patch Management is disabled from the policy, or no maintenance window is configured.'
            ),
            barColor: 'var(--orange-alert-chart-100)',
          },
          {
            key: 'noCheckSinceLastMonth',
            name: translateFn("Didn't check for patches in the last 30 days"),
            tooltipText: translateFn('Endpoints have not checked for available patches in the last 30 days.'),
            barColor: 'var(--yellow-alert-050)',
          },
        ],
      },
      {
        key: 'missingPatches',
        items: [
          {
            key: 'nonSecurity',
            name: translateFn('Missing non-security patches'),
            tooltipText: translateFn('Endpoints reported that new non-security patches are available.'),
            barColor: 'var(--yellow-alert-050)',
          },
          {
            key: 'security',
            name: translateFn('Missing security patches'),
            tooltipText: translateFn('Endpoints reported that new security patches are available.'),
            barColor: 'var(--red-chart-600)',
          },
        ],
      },
    ],
  };

  @query('#patch-management')
  private patchContainer: HTMLElement;

  @query('.info-message')
  private infoMessageContainer: HTMLElement;

  @queryAll('.patch-item')
  private patchItems: HTMLElement[];

  private verticalPadding: number;
  private patchItemsCount: number;

  static get styles() {
    return styles;
  }

  firstUpdated(): void {
    super.firstUpdated();
    const { verticalPadding } = this.getPaddingConstants();
    this.verticalPadding = verticalPadding;

    this.sectionItemVisibilityOnInfoState();
  }

  async updated(): Promise<void> {
    await this.updateComplete;
    this.sectionItemVisibilityOnInfoState();
  }

  protected renderLoader(): TemplateResult {
    return html`
      <div class="loading-container">
        <div class="loading-header">
          <gz-loader></gz-loader>
        </div>
        ${this.strings.sections.map(section => this.renderSectionLoader(section))}
      </div>
    `;
  }

  protected renderSectionLoader(section: StringSection): TemplateResult {
    return html` <div class="loading-section"><gz-loader rows="${section.items.length}"></gz-loader></div>`;
  }

  protected renderChangedInfoBox(text?: string): TemplateResult | undefined {
    if (this.state !== WidgetStateFrontEnd.IS_CHANGED) {
      return;
    }
    return html`<div class="changed-info-box">${super.renderChangedInfoBox(text)}</div>`;
  }

  private sectionItemVisibilityOnInfoState() {
    if (!this.parentElement || !this.patchContainer) {
      return;
    }

    this.patchItems.forEach(item => {
      removeElementClass(item, 'hidden');
    });

    const availableHeight = this.parentElement.offsetHeight - this.verticalPadding * 2;
    const patchContainerHeight =
      this.getWidgetHeaderHeight(this.patchContainer) +
      this.infoMessageContainer.offsetHeight +
      this.patchContainer.offsetHeight;
    const offsetHeight = patchContainerHeight - availableHeight;
    this.patchItemsCount = this.getHiddenItemsCount(offsetHeight);

    if (availableHeight < patchContainerHeight && this.state === WidgetStateFrontEnd.IS_INFO) {
      const startIndex = this.patchItems.length - this.patchItemsCount;

      // Add 'hidden' class to items starting from startIndex to the end
      for (let i = startIndex; i < this.patchItems.length; i++) {
        setElementClass(this.patchItems[i], 'hidden');
      }
    }
  }

  private getHiddenItemsCount(extraHeight: number): number {
    const itemHeight = this.patchItems[0]?.offsetHeight;

    if (!itemHeight || extraHeight < 0) {
      // Handle the case where there are no items or item height is not available
      return 0;
    }

    let totalHeight = 0;
    let itemsToHide = 0;

    for (const item of this.patchItems) {
      totalHeight += itemHeight;
      itemsToHide++;

      if (totalHeight > extraHeight + itemHeight / 2) {
        break;
      }
    }

    return itemsToHide;
  }

  protected handleResize(): void {
    super.handleResize();

    if (!this.parentElement) {
      return;
    }

    this.sectionItemVisibilityOnInfoState();
  }

  protected renderContent(): TemplateResult {
    return html`<div class="info-message">${this.renderNoLicenseInfoBox()}</div>
      ${this.state !== WidgetStateFrontEnd.IS_INFO ? this.renderHeader() : nothing}
      ${this.renderChangedInfoBox(this.changedInfoBoxText)}
      <div id="patch-management">${this.strings.sections.map(section => this.renderSection(section))}</div>`;
  }

  protected renderNoLicenseInfoBox(): TemplateResult | undefined {
    const hasInheritedLicense = (this.payload?.patchManagement as HealthDashboardInfoStateData)?.hasInheritedLicense;
    let noLicenseText = translateFn(
      "No information available. To retrieve data about your company's {widgetName}, please consider a compatible {licenseLink} key.",
      {
        widgetName: '<b>' + getTranslatedWidgetTitle(WEB_COMPONENT_HEALTH_DASHBOARD_PATCH_MANAGEMENT) + '</b>',
        licenseLink: `<gz-link type="underline" href="https://www.bitdefender.com/business/compare.html"><span>${translateFn(
          'license'
        )}</span></gz-link>`,
      }
    );

    if (hasInheritedLicense) {
      noLicenseText = translateFn(
        "To retrieve data about your company's {widgetName}, please contact your Bitdefender Partner and upgrade the licensing plan.",
        {
          widgetName: '<b>' + getTranslatedWidgetTitle(WEB_COMPONENT_HEALTH_DASHBOARD_PATCH_MANAGEMENT) + '</b>',
        }
      );
    }

    return super.renderNoLicenseInfoBox(noLicenseText);
  }

  private renderHeader(): TemplateResult {
    const activeStateData = this.payload?.patchManagement as PatchManagementInfo;
    return html`
      <div class="header">
        ${this.renderHeaderItem(translateFn('Total'), activeStateData?.patchManagementEndpointsCount ?? 0)}
      </div>
    `;
  }

  private renderHeaderItem(name: string, value?: number): TemplateResult {
    return html`
      <gz-widget-header-item
        .name="${name}"
        .value="${value ?? 0}"
        .size="${this.setWidgetHeaderItemSize()}"
        .state="${this.state}"
      ></gz-widget-header-item>
    `;
  }

  protected setWidgetHeaderItemSize(): WidgetHeaderItemSize {
    if (!this.hasResizeLimits() && this.currentWidthInterval[1] === this.resizeBreakingPoints[0]) {
      return WidgetHeaderItemSize.MEDIUM;
    } else {
      return WidgetHeaderItemSize.BIG;
    }
  }

  protected renderSection(section: StringSection): TemplateResult {
    return html` <div class="section">${section.items.map(item => this.renderSectionItem(section, item))}</div> `;
  }

  protected renderSectionItem(section: StringSection, item: PatchManagementItem): TemplateResult {
    const noLicenseResponse = this.payload ? 'hasInheritedLicense' in this.payload.patchManagement : false;
    const value = !noLicenseResponse && this.payload ? this.payload.patchManagement[section.key][item.key] : 0;
    const maxValue =
      !noLicenseResponse && this.payload
        ? (this.payload.patchManagement as PatchManagementInfo).patchManagementEndpointsCount
        : 0;
    if (!this.hasResizeLimits() && this.currentWidthInterval[1] === this.resizeBreakingPoints[0]) {
      return html`
        <gz-widget-section-item
          class="patch-item"
          .name=${item.name}
          .value=${value}
          .state=${this.state}
          itemTooltipText="${item.tooltipText}"
        />
      `;
    } else {
      return html`
        <gz-widget-section-item
          class="patch-item"
          .name=${item.name}
          .value=${value}
          .progressMaxValue=${maxValue}
          .progressValue=${value}
          .progressColor=${item.barColor}
          .state=${this.state}
          itemTooltipText="${item.tooltipText}"
        />
      `;
    }
  }
}
